import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useControls } from 'react-zoom-pan-pinch';
import { useExpandedFdocContentContext } from '../ExpandedFdocContentContext.js';
import { usePDFContent } from './PDF.js';
import { isInMobile, downloadFile } from '@fabric-app/compat';
import { IconPrint } from '@fabric-app/icons/old';
import { styled } from 'styled-components';
import { toast } from '../../../../store/alerts.js';
import { IconLinkArrow, IconDownload } from '@fabric-app/icons/svg';
import { useClientLayoutEffect } from '@fabric-app/hooks/useClientLayoutEffect';

const ZoomPageWrapper = styled.div.withConfig({
  displayName: "HeaderControls__ZoomPageWrapper",
  componentId: "sc-e05affd1-0"
})([
  `background-color:#fcfcfb;padding:6px 16px 6px 16px;border-radius:7px;display:flex;align-items:center;justify-content:center;gap:20px;margin-right:48px;`
]);
const PageTracker = styled.div.withConfig({
  displayName: "HeaderControls__PageTracker",
  componentId: "sc-e05affd1-1"
})([
  `display:flex;gap:8px;color:#343434;font-size:14px;font-weight:400;line-height:17px;letter-spacing:0em;text-align:center;`
]);
const Divider = styled.div.withConfig({
  displayName: "HeaderControls__Divider",
  componentId: "sc-e05affd1-2"
})([
  `height:15px;width:1px;background-color:#cdcdcd;`
]);
const ZoomControl = styled.div.withConfig({
  displayName: "HeaderControls__ZoomControl",
  componentId: "sc-e05affd1-3"
})([
  `display:flex;align-items:center;gap:18px;`
]);
const ZoomIcon = styled.span.withConfig({
  displayName: "HeaderControls__ZoomIcon",
  componentId: "sc-e05affd1-4"
})([
  `font-size:18px;font-weight:500;line-height:16px;letter-spacing:0em;text-align:center;cursor:pointer;user-select:none;-webkit-user-select:none;`
]);
const CurrentPercentage = styled.span.withConfig({
  displayName: "HeaderControls__CurrentPercentage",
  componentId: "sc-e05affd1-5"
})([
  `font-size:14px;font-weight:400;line-height:17px;letter-spacing:0em;text-align:center;display:flex;justify-content:center;min-width:38px;`
]);
const HeaderControls = ({ pdf, isOwner, fileName, storedFileUrl }) => {
  const { topHeaderControlsRef, isFullscreen, setExtraDropdownMenuItems } = useExpandedFdocContentContext();
  const { totalPages, currentPage, scale } = usePDFContent();
  const { zoomIn, zoomOut } = useControls();
  const zoomLevel = useMemo(() => scale * 100, [
    scale
  ]);
  const previousFullscreen = useRef(isFullscreen);
  useEffect(() => {
    if (previousFullscreen.current === isFullscreen) return;
    previousFullscreen.current = isFullscreen;
    if (!isFullscreen) setTimeout(() => zoomIn(0.01), 1);
  }, [
    isFullscreen,
    zoomIn
  ]);
  const onDownload = async () => {
    try {
      if (isInMobile()) {
        if (!storedFileUrl) return;
        downloadFile(storedFileUrl, fileName, "application/pdf");
        return;
      }
      toast({
        content: "Downloading..."
      });
      const data = await pdf.getData();
      const link = document.createElement("a");
      const blob = new Blob([
        data
      ], {
        type: "application/pdf"
      });
      const objectUrl = window.URL.createObjectURL(blob);
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
      }, 100);
    } catch (error) {
      console.error("There was a problem with the fetch operation:", error);
    }
  };
  const zoomControls = topHeaderControlsRef && isFullscreen ? /* @__PURE__ */ createPortal(/* @__PURE__ */ jsx(Fragment, {
    children: /* @__PURE__ */ jsxs(ZoomPageWrapper, {
      children: [
        /* @__PURE__ */ jsxs(PageTracker, {
          children: [
            /* @__PURE__ */ jsx("span", {
              children: totalPages ? currentPage : 0
            }),
            " /",
            /* @__PURE__ */ jsx("span", {
              children: totalPages
            })
          ]
        }),
        /* @__PURE__ */ jsx(Divider, {}),
        /* @__PURE__ */ jsxs(ZoomControl, {
          children: [
            /* @__PURE__ */ jsx(ZoomIcon, {
              onClick: () => {
                zoomOut();
              },
              style: {
                userSelect: "none"
              },
              children: "-"
            }),
            /* @__PURE__ */ jsxs(CurrentPercentage, {
              children: [
                Math.floor(zoomLevel),
                "%"
              ]
            }),
            /* @__PURE__ */ jsx(ZoomIcon, {
              onClick: () => {
                zoomIn();
              },
              style: {
                userSelect: "none"
              },
              children: "+"
            })
          ]
        })
      ]
    })
  }), topHeaderControlsRef) : null;
  const iframeRef = useRef(null);
  useEffect(() => {
    return () => {
      if (!iframeRef.current) return;
      document.body.removeChild(iframeRef.current);
    };
  }, []);
  const onPrint = async () => {
    if (!iframeRef.current) {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      document.body.appendChild(iframe);
      iframeRef.current = iframe;
    }
    const data = await pdf.getData();
    const blob = new Blob([
      data
    ], {
      type: "application/pdf"
    });
    const objectUrl = window.URL.createObjectURL(blob);
    iframeRef.current.src = objectUrl;
    iframeRef.current.onload = () => {
      try {
        iframeRef.current?.contentWindow?.print();
      } catch (error) {
        console.error("Error printing PDF:", error);
      }
    };
  };
  const onOpenExternal = async () => {
    const data = await pdf.getData();
    const blob = new Blob([
      data
    ], {
      type: "application/pdf"
    });
    const objectUrl = window.URL.createObjectURL(blob);
    window.open(objectUrl);
    window.URL.revokeObjectURL(objectUrl);
  };
  useClientLayoutEffect(() => {
    setExtraDropdownMenuItems([
      {
        type: "item",
        key: "open-external",
        onSelect: onOpenExternal,
        children: /* @__PURE__ */ jsxs(Fragment, {
          children: [
            /* @__PURE__ */ jsx(IconLinkArrow, {
              style: {
                width: 12,
                height: 12
              }
            }),
            " Open in browser"
          ]
        })
      },
      {
        type: "item",
        key: "download",
        onSelect: onDownload,
        children: /* @__PURE__ */ jsxs(Fragment, {
          children: [
            /* @__PURE__ */ jsx(IconDownload, {
              style: {
                width: 12,
                height: 12
              }
            }),
            " Download"
          ]
        })
      },
      {
        type: "item",
        key: "print",
        onSelect: onPrint,
        children: /* @__PURE__ */ jsxs(Fragment, {
          children: [
            /* @__PURE__ */ jsx(IconPrint, {
              style: {
                width: 12,
                height: 12
              }
            }),
            " Print"
          ]
        })
      },
      ...isOwner ? [
        {
          type: "separator",
          key: "separator"
        }
      ] : []
    ]);
    return () => {
      setExtraDropdownMenuItems([]);
    };
  }, []);
  return /* @__PURE__ */ jsx(Fragment, {
    children: zoomControls
  });
};

export { HeaderControls as default };
//# sourceMappingURL=HeaderControls.js.map
